import { useState } from 'react';
import { Plus, Minus } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';

const FAQS = [
  {
    id: 'what-is-miso-mini',
    q: 'What exactly is Miso Mini?',
    a: 'Miso Mini is a premium portable photo booth designed for weddings, birthdays, and corporate events. It takes beautiful photos and prints them in 20 seconds — your guests walk away with a physical keepsake.',
  },
  {
    id: 'advance',
    q: 'How far in advance should I book?',
    a: 'We recommend booking 2–4 weeks ahead, especially during wedding season (Oct–Mar). For urgent bookings, reach out on WhatsApp and we\'ll try our best to accommodate.',
  },
  {
    id: 'included',
    q: "What's included in the ₹18,000 price?",
    a: 'Everything — Miso Mini setup, unlimited prints, a custom template with your event details, an on-site MISO operator, setup & takedown, and free delivery within Hyderabad. No hidden costs.',
  },
  {
    id: 'space',
    q: 'How much space does Miso Mini need?',
    a: "Miso Mini is compact — it fits in a 4×4ft area. We'll confirm exact space requirements when you book.",
  },
  {
    id: 'travel',
    q: 'Do you travel outside Hyderabad?',
    a: 'Yes — we cover all of Telangana. A travel fee applies for venues 50km+ from the city.',
  },
  {
    id: 'cancel',
    q: "What's the cancellation policy?",
    a: 'Free cancellation up to 7 days before your event. The advance is fully refundable within this window.',
  },
  {
    id: 'other-products',
    q: 'What are Guest Booth and Miso Experience?',
    a: 'These are our upcoming offerings — Guest Booth (a video guestbook station) and Miso Experience (the full MISO setup). They\'re not available to book yet, but they\'re coming soon. Follow @miso.hyd on Instagram for updates.',
  },
];

function FAQItem({ faq, open, onToggle, index }) {
  const reveal = useScrollReveal(index * 0.06);

  return (
    <div
      ref={reveal.ref}
      style={{ ...reveal.style, borderBottom: '1px solid rgba(139,74,48,0.12)' }}
    >
      <button
        onClick={onToggle}
        data-testid={`faq-toggle-${faq.id}`}
        aria-expanded={open}
        style={{
          width: '100%', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between',
          padding: '22px 0', background: 'none', border: 'none',
          cursor: 'pointer', textAlign: 'left', gap: '16px',
        }}
      >
        <span
          style={{
            fontFamily: 'Graphik, sans-serif',
            fontWeight: 700, fontSize: 'clamp(15px, 2vw, 19px)',
            color: '#2D2018', lineHeight: 1.3,
          }}
        >
          {faq.q}
        </span>
        <span
          style={{
            flexShrink: 0, width: '32px', height: '32px', borderRadius: '50%',
            border: '1px solid rgba(139,74,48,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#B85C3A',
            backgroundColor: open ? 'rgba(184,92,58,0.08)' : 'transparent',
            transition: 'background-color 0.2s ease',
          }}
        >
          {open ? <Minus size={16} strokeWidth={2} /> : <Plus size={16} strokeWidth={2} />}
        </span>
      </button>
      <div
        data-testid={`faq-answer-${faq.id}`}
        style={{
          overflow: 'hidden',
          maxHeight: open ? '300px' : '0',
          opacity: open ? 1 : 0,
          transition: 'max-height 0.4s ease, opacity 0.3s ease',
        }}
      >
        <p className="body-miso" style={{ paddingBottom: '24px', fontSize: '15px' }}>
          {faq.a}
        </p>
      </div>
    </div>
  );
}

export default function FAQSection() {
  const [openId, setOpenId] = useState(null);
  const heading = useScrollReveal(0);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="section-pad"
      style={{ backgroundColor: '#F2EBE0' }}
    >
      <div className="container-miso">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '64px',
            alignItems: 'start',
          }}
        >
          {/* Heading — sticky on desktop */}
          <div style={{ position: 'sticky', top: '100px' }}>
            <span className="section-label">Questions</span>
            <h2 ref={heading.ref} style={heading.style} className="h2-miso">
              Everything you want to{' '}
              <span className="italic-accent" style={{ color: '#B85C3A' }}>know.</span>
            </h2>
            <p className="body-miso" style={{ marginTop: '16px', fontSize: '14px' }}>
              Still curious? We&apos;re one message away on WhatsApp or Instagram.
            </p>
          </div>

          {/* Accordion */}
          <div>
            {FAQS.map((faq, i) => (
              <FAQItem
                key={faq.id}
                faq={faq}
                index={i}
                open={openId === faq.id}
                onToggle={() => setOpenId(openId === faq.id ? null : faq.id)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
